/**
 * Navegação ERP — fonte única para Sidebar (desktop) e MobileBottomNav.
 */

import type { ComponentType } from 'react';
import {
  LayoutDashboard,
  Users,
  User,
  Building2,
  UserCog,
  Shield,
  Package,
  Grid,
  Folder,
  Tag,
  Ruler,
  RefreshCw,
  FileText,
  RotateCcw,
  ShoppingCart,
  ShoppingBag,
  CreditCard,
  FileEdit,
  Receipt,
  Wallet,
  Truck,
  TrendingUp,
  TrendingDown,
  FolderTree,
  FileCheck,
  Plus,
  FolderOpen,
  FileHeart,
  Fingerprint,
  ShieldEllipsis,
  Target,
  Table,
  ChartPie,
  Rocket,
  MapPin,
  DatabaseBackup,
  MessageSquare,
  Activity,
  Download,
  Landmark,
  Scale,
  LayersPlus,
  Monitor,
  ClipboardList,
  Calendar,
  CalendarHeart,
  Settings,
  WandSparkles,
  Printer,
  FileBarChart,
  HeartPulse,
} from 'lucide-react';
import { MODULES, ACTIONS } from '../hooks/usePermissions';
import { canAccessErpReport, canAccessReportsHub } from '../utils/permissions';
import { ERP_REPORTS_CATALOG } from './erpReportsCatalog';

export type ErpIcon = ComponentType<{ size?: number }>;

export interface ErpSubMenuItem {
  label: string;
  path: string;
  icon?: ErpIcon;
}

export interface ErpMenuItem {
  id: string;
  label: string;
  icon: ErpIcon;
  path?: string;
  submenu?: ErpSubMenuItem[];
}

export const ERP_MENU_ITEMS: ErpMenuItem[] = [
  {
    id: 'cockpit',
    label: 'Cockpit',
    icon: Rocket,
    path: '/erp/cockpit',
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: '/erp/dashboard',
    submenu: [
      { label: 'Visão geral', path: '/erp/dashboard', icon: LayoutDashboard },
      { label: 'Análise de Atividades', path: '/erp/dashboard/atividades', icon: Activity },
    ],
  },
  {
    id: 'pacientes',
    label: 'Pacientes',
    icon: HeartPulse,
    path: '/erp/pacientes',
  },
  {
    id: 'painel-pacientes',
    label: 'Painel clínico',
    icon: Activity,
    path: '/erp/pacientes/painel',
  },
  {
    id: 'plantoes',
    label: 'Plantões',
    icon: Calendar,
    path: '/erp/plantoes',
  },
  {
    id: 'minha-escala',
    label: 'Meus plantões',
    icon: CalendarHeart,
    path: '/erp/minha-escala',
  },
  {
    id: 'pessoas',
    label: 'Pessoas',
    icon: Users,
    path: '/erp/pessoas',
    submenu: [
      { label: 'Clientes', path: '/erp/pessoas/clientes', icon: User },
      { label: 'Fornecedores', path: '/erp/pessoas/fornecedores', icon: Building2 },
      { label: 'Usuários', path: '/erp/pessoas/usuarios', icon: UserCog },
      { label: 'Grupos de Acesso', path: '/erp/pessoas/grupos', icon: Shield },
    ],
  },
  {
    id: 'produtos',
    label: 'Produtos',
    icon: Package,
    path: '/erp/produtos',
    submenu: [
      { label: 'Produtos', path: '/erp/produtos/produtos', icon: Package },
      { label: 'Etiquetas', path: '/erp/produtos/etiquetas', icon: Printer },
      { label: 'Solicitações de Criação', path: '/erp/produtos/solicitacoes', icon: ClipboardList },
      { label: 'Grade de Produtos', path: '/erp/produtos/grades', icon: Grid },
      { label: 'Grupos', path: '/erp/produtos/grupos', icon: Folder },
      { label: 'Marcas', path: '/erp/produtos/marcas', icon: Tag },
      { label: 'Unidades', path: '/erp/produtos/unidades', icon: Ruler },
    ],
  },
  {
    id: 'estoque',
    label: 'Estoque',
    icon: DatabaseBackup,
    path: '/erp/estoque',
    submenu: [
      { label: 'Dashboard Estoque', path: '/erp/stock/incoming', icon: Download },
      { label: 'Ajuste de Estoque', path: '/erp/stock/adjustments', icon: LayersPlus },
      { label: 'Movimentações', path: '/erp/estoque/movimentacoes', icon: RefreshCw },
      { label: 'Auditoria de Estoque', path: '/erp/stock/audits', icon: FileCheck },
      { label: 'Gestão de Endereços', path: '/erp/stock/locations', icon: MapPin },
      { label: 'Trocas/Devoluções', path: '/erp/estoque/trocas', icon: RotateCcw },
    ],
  },
  {
    id: 'vendas',
    label: 'Vendas',
    icon: ShoppingCart,
    path: '/erp/vendas',
    submenu: [
      { label: 'Vendas', path: '/erp/vendas/vendas', icon: ShoppingCart },
      { label: 'Relação de Vendas', path: '/erp/vendas/relacao-vendas', icon: FileText },
      { label: 'Pré-Venda', path: '/erp/vendas/pre-venda', icon: CreditCard },
      { label: 'Relação de Pré-Venda', path: '/erp/vendas/relacao-pre-venda', icon: FileEdit },
      { label: 'Vendas Online', path: '/erp/vendas/online', icon: ShoppingBag },
      { label: 'NF-e', path: '/erp/vendas/nfe', icon: Receipt },
      { label: 'Tabelas Especiais', path: '/erp/vendas/tabelas-especiais', icon: Table },
      { label: 'Conferência de Caixa', path: '/erp/vendas/caixa', icon: Wallet },
      { label: 'Vitrine TV', path: '/erp/vendas/tv-display', icon: Monitor },
    ],
  },
  {
    id: 'orcamentos',
    label: 'Orçamentos',
    icon: FileHeart,
    path: '/erp/orcamentos',
    submenu: [
      { label: 'Dashboard', path: '/erp/orcamentos/dashboard', icon: LayoutDashboard },
      { label: 'Listagem', path: '/erp/orcamentos', icon: FileText },
      { label: 'Novo Orçamento', path: '/erp/orcamentos/novo', icon: Plus },
    ],
  },
  {
    id: 'compras',
    label: 'Compras',
    icon: ShoppingBag,
    path: '/erp/purchases',
    submenu: [
      { label: 'Compra Inteligente', path: '/erp/purchases/intelligence', icon: WandSparkles },
      { label: 'Painel de Compras', path: '/erp/purchases', icon: LayoutDashboard },
      { label: 'Acompanhamento', path: '/erp/purchases/follow-up', icon: Truck },
    ],
  },
  {
    id: 'financeiro',
    label: 'Financeiro',
    icon: Wallet,
    path: '/erp/financeiro',
    submenu: [
      { label: 'Dashboard Financeiro', path: '/erp/financeiro/dashboard', icon: ChartPie },
      { label: 'Análise Fluxo de Caixa', path: '/erp/financeiro/analise-fluxo-caixa', icon: ChartPie },
      { label: 'Análise Contas a Pagar', path: '/erp/financeiro/analise-contas-pagar', icon: TrendingDown },
      { label: 'DRE', path: '/erp/financeiro/dre', icon: FileText },
      { label: 'DFC', path: '/erp/financeiro/dfc', icon: FileText },
      { label: 'Balanço Contábil', path: '/erp/financeiro/balanco', icon: Scale },
      { label: 'Contas a Receber', path: '/erp/financeiro/contas-receber', icon: TrendingUp },
      { label: 'Contas a Pagar', path: '/erp/financeiro/contas-pagar', icon: TrendingDown },
      { label: 'Dashboard de Boletos', path: '/erp/financeiro/boletos/dashboard', icon: ChartPie },
      { label: 'Boletos', path: '/erp/financeiro/boletos', icon: FileText },
      { label: 'Aprovação Financeira', path: '/erp/purchases/finance', icon: CreditCard },
      { label: 'Extrato de Caixa', path: '/erp/financeiro/extrato-caixa', icon: Landmark },
      { label: 'Conciliação de Cartões', path: '/erp/financeiro/conciliacao-cartoes', icon: CreditCard },
      { label: 'Contas', path: '/erp/financeiro/contas', icon: Wallet },
      { label: 'Categorias', path: '/erp/financeiro/categorias', icon: FolderTree },
      { label: 'Centros de Custo', path: '/erp/financeiro/centros-custo', icon: Building2 },
      { label: 'Formas de Recebimento', path: '/erp/financeiro/formas-recebimento', icon: CreditCard },
    ],
  },
  {
    id: 'relatorios',
    label: 'Relatórios',
    icon: FileBarChart,
    path: '/erp/relatorios',
  },
  {
    id: 'configuracoes',
    label: 'Configurações',
    icon: Settings,
    path: '/erp/configuracoes',
  },
  {
    id: 'documentos',
    label: 'Documentos',
    icon: FolderOpen,
    path: '/erp/documents',
    submenu: [
      { label: 'Dashboard', path: '/erp/documents/dashboard', icon: LayoutDashboard },
      { label: 'Documentos da Empresa', path: '/erp/documents/company', icon: FileText },
      { label: 'Documentos dos Usuários', path: '/erp/documents/users', icon: FileCheck },
      { label: 'Extrato de Ponto', path: '/erp/documents/attendance-reports', icon: ShieldEllipsis },
      { label: 'Espelho de Ponto', path: '/erp/documents/attendance-log', icon: ClipboardList },
      { label: 'Categorias', path: '/erp/documents/categories', icon: Tag },
    ],
  },
  {
    id: 'prospeccao',
    label: 'Prospecção',
    icon: Target,
    path: '/erp/prospecting',
    submenu: [
      { label: 'Hub de Prospecção', path: '/erp/prospecting', icon: Target },
      { label: 'Fluxo de Comunicações', path: '/erp/prospecting/contacts', icon: MessageSquare },
    ],
  },
  {
    id: 'ponto-eletronico',
    label: 'Ponto Eletrônico',
    icon: Fingerprint,
    path: '/erp/ponto-eletronico',
  },
];

/** Rotas extras acessíveis no mobile (não listadas no sidebar desktop). */
export const ERP_MOBILE_EXTRA_ITEMS: ErpMenuItem[] = [
  {
    id: 'mensagens',
    label: 'Mensagens',
    icon: MessageSquare,
    path: '/erp/mensagens',
  },
];

export const ERP_ROUTE_PERMISSIONS: Record<string, { module: string; action: string }> = {
  '/erp/cockpit': { module: MODULES.COCKPIT, action: ACTIONS.VIEW },
  '/erp/dashboard': { module: MODULES.DASHBOARD, action: ACTIONS.VIEW },
  '/erp/dashboard/atividades': { module: MODULES.DASHBOARD, action: ACTIONS.VIEW },
  '/erp/mensagens': { module: MODULES.MESSAGES, action: ACTIONS.VIEW },
  '/erp/pacientes': { module: MODULES.PATIENTS, action: ACTIONS.VIEW },
  '/erp/pacientes/painel': { module: MODULES.PATIENTS, action: ACTIONS.PATIENTS_DASHBOARD },
  '/erp/plantoes': { module: MODULES.DUTY_ROSTERS, action: ACTIONS.VIEW },
  '/erp/minha-escala': { module: MODULES.MY_SHIFTS, action: ACTIONS.VIEW },
  '/erp/pessoas/clientes': { module: MODULES.PEOPLE, action: ACTIONS.PEOPLE_CUSTOMERS },
  '/erp/pessoas/fornecedores': { module: MODULES.PEOPLE, action: ACTIONS.PEOPLE_SUPPLIERS },
  '/erp/pessoas/usuarios': { module: MODULES.PEOPLE, action: ACTIONS.PEOPLE_USERS },
  '/erp/pessoas/grupos': { module: MODULES.PEOPLE, action: ACTIONS.PEOPLE_ACCESS_GROUPS },
  '/erp/produtos/produtos': { module: MODULES.PRODUCTS, action: ACTIONS.VIEW },
  '/erp/produtos/etiquetas': { module: MODULES.PRODUCTS, action: ACTIONS.VIEW },
  '/erp/produtos/solicitacoes': { module: MODULES.PRODUCTS, action: ACTIONS.PRODUCT_REQUEST_VIEW },
  '/erp/produtos/grades': { module: MODULES.PRODUCTS, action: ACTIONS.PRODUCTS_GRIDS },
  '/erp/produtos/grupos': { module: MODULES.PRODUCTS, action: ACTIONS.PRODUCTS_GROUPS },
  '/erp/produtos/marcas': { module: MODULES.PRODUCTS, action: ACTIONS.PRODUCTS_BRANDS },
  '/erp/produtos/unidades': { module: MODULES.PRODUCTS, action: ACTIONS.PRODUCTS_UNITS },
  '/erp/estoque/movimentacoes': { module: MODULES.STOCK, action: ACTIONS.STOCK_MOVEMENTS },
  '/erp/stock/incoming': { module: MODULES.STOCK, action: ACTIONS.STOCK_IMPORT_INVOICE },
  '/erp/stock/adjustments': { module: MODULES.STOCK, action: ACTIONS.STOCK_ADJUSTMENT_VIEW },
  '/erp/stock/audits': { module: MODULES.STOCK, action: ACTIONS.STOCK_AUDIT_VIEW },
  '/erp/stock/audits/list': { module: MODULES.STOCK, action: ACTIONS.STOCK_AUDIT_VIEW },
  '/erp/stock/locations': { module: MODULES.STOCK, action: ACTIONS.STOCK_INVOICES },
  '/erp/estoque/trocas': { module: MODULES.STOCK, action: ACTIONS.STOCK_RETURNS_VIEW },
  '/erp/vendas/vendas': { module: MODULES.SALES, action: ACTIONS.SALES_PDV_VIEW },
  '/erp/vendas/relacao-vendas': { module: MODULES.SALES, action: ACTIONS.SALES_RELATION_VIEW },
  '/erp/vendas/pre-venda': { module: MODULES.SALES, action: ACTIONS.SALES_PRESALE },
  '/erp/vendas/relacao-pre-venda': { module: MODULES.SALES, action: ACTIONS.SALES_PRESALE_LIST_VIEW },
  '/erp/vendas/online': { module: MODULES.SALES, action: ACTIONS.SALES_ONLINE_VIEW },
  '/erp/vendas/nfe': { module: MODULES.SALES, action: ACTIONS.SALES_NFE_VIEW },
  '/erp/vendas/tabelas-especiais': { module: MODULES.SALES, action: ACTIONS.SALES_SPECIAL_TABLES_VIEW },
  '/erp/vendas/caixa': { module: MODULES.SALES, action: ACTIONS.SALES_CASH_REGISTER_RECONCILIATION },
  '/erp/vendas/tv-display': { module: MODULES.SALES, action: ACTIONS.SALES_TV_DISPLAY },
  '/erp/orcamentos': { module: MODULES.QUOTES, action: ACTIONS.VIEW },
  '/erp/orcamentos/dashboard': { module: MODULES.QUOTES, action: ACTIONS.VIEW },
  '/erp/orcamentos/novo': { module: MODULES.QUOTES, action: ACTIONS.CREATE },
  '/erp/financeiro/dashboard': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_CASH_FLOW },
  '/erp/financeiro/analise-fluxo-caixa': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_CASH_FLOW },
  '/erp/financeiro/analise-contas-pagar': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_PAYABLES },
  '/erp/financeiro/dre': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_DRE },
  '/erp/financeiro/dfc': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_DFC },
  '/erp/financeiro/balanco': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_BALANCE_SHEET },
  '/erp/financeiro/contas-receber': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_RECEIVABLES_VIEW },
  '/erp/financeiro/contas-pagar': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_PAYABLES },
  '/erp/financeiro/boletos/dashboard': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_BOLETOS_DASHBOARD },
  '/erp/financeiro/boletos': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_BOLETOS_VIEW },
  '/erp/purchases/finance': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_PURCHASE_APPROVAL },
  '/erp/financeiro/extrato-caixa': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_CASH_FLOW },
  '/erp/financeiro/conciliacao-cartoes': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_CARD_RECONCILIATION },
  '/erp/financeiro/contas': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_ACCOUNTS },
  '/erp/financeiro/categorias': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_CATEGORIES },
  '/erp/financeiro/centros-custo': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_COST_CENTERS },
  '/erp/financeiro/formas-recebimento': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_RECEIPT_METHODS },
  '/erp/documents/dashboard': { module: MODULES.DOCUMENTS, action: ACTIONS.DOCUMENTS_DASHBOARD },
  '/erp/documents/company': { module: MODULES.DOCUMENTS, action: ACTIONS.DOCUMENTS_COMPANY_VIEW },
  '/erp/documents/users': { module: MODULES.DOCUMENTS, action: ACTIONS.DOCUMENTS_USER_VIEW },
  '/erp/documents/attendance-reports': { module: MODULES.ATTENDANCE, action: ACTIONS.ATTENDANCE_ACCOUNTING },
  '/erp/documents/attendance-log': { module: MODULES.ATTENDANCE, action: ACTIONS.ATTENDANCE_LOG },
  '/erp/documents/categories': { module: MODULES.DOCUMENTS, action: ACTIONS.DOCUMENTS_CATEGORIES_VIEW },
  '/erp/ponto-eletronico': { module: MODULES.ATTENDANCE, action: ACTIONS.VIEW },
  '/erp/prospecting': { module: MODULES.PROSPECTING, action: ACTIONS.PROSPECTING_VIEW },
  '/erp/prospecting/contacts': { module: MODULES.PROSPECTING, action: ACTIONS.PROSPECTING_VIEW },
  '/erp/prospecting/proposals/new': { module: MODULES.PROSPECTING, action: ACTIONS.PROSPECTING_PROPOSALS },
  '/erp/purchases': { module: MODULES.PURCHASES, action: ACTIONS.PURCHASES_DASHBOARD },
  '/erp/purchases/intelligence': { module: MODULES.PURCHASES, action: ACTIONS.PURCHASES_INTELLIGENCE },
  '/erp/purchases/follow-up': { module: MODULES.PURCHASES, action: ACTIONS.PURCHASES_FOLLOW_UP },
  '/erp/purchases/mirrors': { module: MODULES.PURCHASES, action: ACTIONS.PURCHASES_DASHBOARD },
  '/erp/relatorios/contas-receber': { module: MODULES.REPORTS, action: ACTIONS.REPORTS_RECEIVABLES },
  '/erp/relatorios/estoque': { module: MODULES.REPORTS, action: ACTIONS.REPORTS_STOCK },
  '/erp/relatorios/vendas': { module: MODULES.REPORTS, action: ACTIONS.REPORTS_SALES },
  '/erp/relatorios': { module: MODULES.REPORTS, action: ACTIONS.VIEW },
  '/erp/configuracoes': { module: MODULES.SETTINGS, action: ACTIONS.VIEW },
  '/erp/configuracoes/cockpit': { module: MODULES.SETTINGS, action: ACTIONS.VIEW },
};

export interface ErpRouteAccessContext {
  isAdmin: boolean;
  permissionsLoading: boolean;
  hasPermission: (module: string, action: string) => boolean;
  hasModuleAccess: (module: string) => boolean;
}

export function canAccessErpRoute(path: string | undefined, ctx: ErpRouteAccessContext): boolean {
  if (!path) return false;
  if (ctx.isAdmin) return true;
  if (ctx.permissionsLoading) return false;

  if (path === '/erp/relatorios' || path.startsWith('/erp/relatorios/')) {
    const catalogMatch = ERP_REPORTS_CATALOG.find((item) => item.path === path);
    if (catalogMatch) {
      return canAccessErpReport(ctx.hasPermission, catalogMatch.action);
    }
    return canAccessReportsHub(ctx.hasPermission);
  }

  if (path === '/erp/pacientes/painel') {
    return ctx.hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_DASHBOARD);
  }

  if (path === '/erp/pacientes' || path.startsWith('/erp/pacientes/')) {
    return ctx.hasPermission(MODULES.PATIENTS, ACTIONS.VIEW);
  }

  if (path === '/erp/configuracoes/certificados') {
    return (
      ctx.hasPermission(MODULES.DIGITAL_CERTIFICATE, ACTIONS.DIGITAL_CERTIFICATE_MANAGE) ||
      ctx.hasPermission(MODULES.SETTINGS, ACTIONS.SETTINGS_CERTIFICATES)
    );
  }

  const permission = ERP_ROUTE_PERMISSIONS[path];
  if (!permission) {
    if (path.startsWith('/erp/estoque/') || path.startsWith('/erp/stock/')) return ctx.hasModuleAccess(MODULES.STOCK);
    if (path.startsWith('/erp/vendas/')) return ctx.hasModuleAccess(MODULES.SALES);
    if (path.startsWith('/erp/produtos/')) return ctx.hasModuleAccess(MODULES.PRODUCTS);
    if (path.startsWith('/erp/pessoas/')) return ctx.hasModuleAccess(MODULES.PEOPLE);
    if (path.startsWith('/erp/financeiro/boletos/dashboard')) {
      return ctx.hasPermission(MODULES.FINANCIAL, ACTIONS.FINANCIAL_BOLETOS_DASHBOARD);
    }
    if (path.startsWith('/erp/financeiro/boletos')) {
      return ctx.hasPermission(MODULES.FINANCIAL, ACTIONS.FINANCIAL_BOLETOS_VIEW);
    }
    if (path.startsWith('/erp/purchases/finance')) {
      return ctx.hasPermission(MODULES.FINANCIAL, ACTIONS.FINANCIAL_PURCHASE_APPROVAL);
    }
    if (path.startsWith('/erp/purchases/follow-up')) {
      return ctx.hasPermission(MODULES.PURCHASES, ACTIONS.PURCHASES_FOLLOW_UP);
    }
    if (path.startsWith('/erp/purchases/intelligence')) {
      return ctx.hasPermission(MODULES.PURCHASES, ACTIONS.PURCHASES_INTELLIGENCE);
    }
    if (path.startsWith('/erp/purchases/')) {
      return ctx.hasPermission(MODULES.PURCHASES, ACTIONS.PURCHASES_DASHBOARD);
    }
    if (path.startsWith('/erp/financeiro/')) return ctx.hasModuleAccess(MODULES.FINANCIAL);
    if (path.startsWith('/erp/documents/')) return ctx.hasModuleAccess(MODULES.DOCUMENTS);
    if (path.startsWith('/erp/prospecting/')) return ctx.hasModuleAccess(MODULES.PROSPECTING);
    if (path.startsWith('/erp/orcamentos/')) return ctx.hasModuleAccess(MODULES.QUOTES);
    if (path.startsWith('/erp/configuracoes/')) return ctx.hasModuleAccess(MODULES.SETTINGS);
    return false;
  }
  return ctx.hasPermission(permission.module, permission.action);
}

export function filterErpMenuItems(items: ErpMenuItem[], ctx: ErpRouteAccessContext): ErpMenuItem[] {
  if (ctx.permissionsLoading) return [];
  return items
    .map((item) => {
      if (!item.submenu) {
        if (!canAccessErpRoute(item.path, ctx)) return null;
        return item;
      }
      const filteredSubmenu = item.submenu.filter((subItem) => canAccessErpRoute(subItem.path, ctx));
      if (filteredSubmenu.length === 0) return null;
      return { ...item, submenu: filteredSubmenu };
    })
    .filter(Boolean) as ErpMenuItem[];
}

/** IDs exibidos na barra inferior fixa (mobile/tablet). Demais módulos ficam em "Mais". */
export const ERP_MOBILE_MAIN_BAR_IDS = ['cockpit', 'dashboard', 'financeiro', 'compras', 'mensagens'] as const;

export function matchErpPath(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

export function getBestMatchingSubmenuPath(pathname: string, submenu: ErpSubMenuItem[]): string | null {
  const matches = submenu
    .map((item) => item.path)
    .filter((path) => matchErpPath(pathname, path))
    .sort((a, b) => b.length - a.length);
  return matches[0] ?? null;
}

export function isErpSubmenuItemActive(
  pathname: string,
  submenu: ErpSubMenuItem[],
  path: string
): boolean {
  return getBestMatchingSubmenuPath(pathname, submenu) === path;
}

export function isErpParentItemActive(pathname: string, item: ErpMenuItem): boolean {
  if (!item.path) return false;
  if (!matchErpPath(pathname, item.path)) return false;
  if (item.submenu?.length) {
    return getBestMatchingSubmenuPath(pathname, item.submenu) === item.path;
  }
  return true;
}
